import React, { useMemo } from 'react';
import cn from 'classnames';
import Modal from 'react-modal';

import { InsufficientFunds } from 'types/componentTypes';
import CustomTooltip from 'components/Tooltips/CustomTooltip';
import HeaderButton from 'components/Popups/InsufficientFundsPopup/PopupComponents/headerButtons';
import ARStatusBar from 'components/Popups/InsufficientFundsPopup/PopupComponents/StatusBar';
import AutoReloadSection from 'components/Popups/InsufficientFundsPopup/PopupComponents/AutoReloadSection';

import style from './style.module.scss';

const InsufficientFundsPopup: React.FC<InsufficientFunds> = ({
  isOpen,
  sideUser,
  psychicChatStatus,
  fundsToAdd,
  acceptButton,
  rejectButton,
  psychicImage,
  isAutoReloadEnabled,
  autoReloadLabels,
  isInfoTooltipVisible,
  isToggleTooltipVisible,
  infoTooltipButtonRef,
  toggleTooltipButtonRef,
  handleAutoReloadChange,
  CurrentBalanceRowLabel,
  RequiredBalanceRowLabel,
  handleToggleTooltipVisible,
  handleInfoTooltipVisible,
  isPrimaryPaymentMethod,
}) => {
  const {
    autoreloadChatButtonText,
    autoreloadChatHeaderText1,
    autoreloadChatToggleOnText1,
    autoreloadChatAsteriskInfoText,
    autoreloadChatHeadTooltipText1,
    autoreloadChatToggleTurnOnText1,
    autoreloadChatToggleTurnOffText1,
    autoreloadChatToggleTooltipText1,
    autoreloadChatToggleTooltipText2,
    autoreloadChatToggleTurnOffInfoText1,
  } = autoReloadLabels;

  const acceptButtonText = useMemo(() => (
    autoreloadChatButtonText?.replace('XX.XX', parseFloat(fundsToAdd).toFixed(2))
  ), [fundsToAdd, autoreloadChatButtonText]);

  const toggleButtonLabel = useMemo(() => (
    isAutoReloadEnabled
      ? autoreloadChatToggleTurnOnText1
      : autoreloadChatToggleTurnOffText1),
  [
    isAutoReloadEnabled,
    autoreloadChatToggleTurnOffText1,
    autoreloadChatToggleTurnOnText1,
  ]);

  const toggleInfoLabel = useMemo(() => (isAutoReloadEnabled
    ? autoreloadChatToggleOnText1
    : autoreloadChatToggleTurnOffInfoText1),
  [
    isAutoReloadEnabled,
    autoreloadChatToggleOnText1,
    autoreloadChatToggleTurnOffInfoText1,
  ]);

  const sliderCn = cn(
    style.autoReloadSlider,
    isAutoReloadEnabled ? style.autoReloadOn : style.autoReloadOff,
  );
  const sliderInner = cn(
    style.autoReloadSliderContent,
    isAutoReloadEnabled ? style.autoReloadTextOn : style.autoReloadTextOff,
  );

  const getBottomMessage = () => isPrimaryPaymentMethod
    && (
      <span className={style.bottomMessage}>
        {autoreloadChatAsteriskInfoText}
      </span>
    );

  return (
    <Modal
      isOpen={isOpen}
      className={style.modal}
      overlayClassName={style.overlay}
      ariaHideApp={false}
    >
      <div className={cn(style.wrapper, style.wrapperWidthBig, style.background)}>
        <HeaderButton onClick={rejectButton.handler} psychicName={sideUser?.friendlyName} />
        <div className={style.content}>
          <img
            alt="psychic"
            src={psychicImage}
            className={style.psychicImg}
          />
          <div className={style.container}
          >
            <ARStatusBar
              status={psychicChatStatus}
              basePrice={sideUser?.basePrice}
              rate={sideUser?.customerPrice || sideUser?.basePrice}
            />
            <div>
              <div className={style.topMessage}>
                {autoreloadChatHeaderText1}
                <button
                  type="button"
                  className={style.topMessageBtn}
                  ref={infoTooltipButtonRef}
                  onClick={handleInfoTooltipVisible()}
                  onBlur={handleInfoTooltipVisible(true)}
                >
                  <span className={style.topMessageInfo}>i</span>
                  <CustomTooltip
                    isOpen={isInfoTooltipVisible}
                    classNames={style.topMessageTooltip}
                  >
                    <span className={style.tooltipCol}>
                      {autoreloadChatHeadTooltipText1}
                    </span>
                    <CurrentBalanceRowLabel style={style} />
                    <RequiredBalanceRowLabel style={style} />
                  </CustomTooltip>
                </button>
              </div>
              <AutoReloadSection
                isPrimaryPaymentMethod={isPrimaryPaymentMethod}
                isAutoReloadEnabled={isAutoReloadEnabled}
                toggleButtonLabel={toggleButtonLabel}
                toggleTooltipButtonRef={toggleTooltipButtonRef}
                handleToggleTooltipVisible={handleToggleTooltipVisible}
                toggleInfoLabel={toggleInfoLabel}
                sliderCn={sliderCn}
                acceptButton={acceptButton}
                acceptButtonText={acceptButtonText}
                sliderInner={sliderInner}
                cancelButtonHadler={rejectButton.handler}
                isToggleTooltipVisible={isToggleTooltipVisible}
                handleAutoReloadChange={handleAutoReloadChange}
                autoreloadChatToggleTooltipText1={autoreloadChatToggleTooltipText1}
                autoreloadChatToggleTooltipText2={autoreloadChatToggleTooltipText2}
              />
            </div>
            {getBottomMessage()}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default InsufficientFundsPopup;
